
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonSpinner,
    IonIcon,
    IonButtons,
    IonButton,
    IonInput,
    menuController,
} from "@ionic/vue";
import { refresh, arrowBackCircle, arrowForwardCircle, menu } from "ionicons/icons";

import { computed, ref, watch } from "vue";

import { LOCALSTORAGE_KEY } from "@/custom_config";

import moment from "moment";

import { dateFormat } from "@/services/utils";
import { openToast } from "@/services/toast";
import apiResponsabile from "@/services/responsabile";

export default {
    name: "RiepilogoPresenze",
    components: {
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        IonPage,
        IonSpinner,
        IonIcon,
        IonButtons,
        IonButton,
        IonInput,
    },
    setup() {
        const responsabileID = JSON.parse(localStorage.getItem(`${LOCALSTORAGE_KEY}`)).dipendenti_id;

        const loading = ref(false);
        const presenze = ref([]);
        const selectedPeriod = ref("settimana"); // Default: settimana
        const selectedMonth = ref(moment().format("YYYY-MM")); // Default: current month

        // Array of months for selector - generate for current year
        const currentYear = moment().year();
        const currentMonth = moment().month() + 1; // moment().month() returns 0-11, we need 1-12
        const months = [];
        const monthNames = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
        
        for (let i = 1; i <= 12; i++) {
            const monthValue = `${currentYear}-${i.toString().padStart(2, '0')}`;
            const isDisabled = i > currentMonth; // Disable future months
            months.push({
                value: monthValue,
                label: monthNames[i - 1],
                disabled: isDisabled
            });
        }

        const openMenu = () => {
            menuController.open("app-menu");
        };

        function setActivePeriod(period: string) {
            selectedPeriod.value = period;
            // Reset to current month when switching to 'mese' filter
            if (period === 'mese') {
                selectedMonth.value = moment().format("YYYY-MM");
            }
        }

        function setActiveMonth(month: string) {
            selectedMonth.value = month;
        }

        function getPeriodLabel() {
            switch (selectedPeriod.value) {
                case "oggi":
                    return moment().format("DD/MM/YYYY");
                case "settimana": {
                    const startWeek = moment().startOf('week').format("DD/MM");
                    const endWeek = moment().endOf('week').format("DD/MM/YYYY");
                    return `${startWeek} - ${endWeek}`;
                }
                case "mese":
                    return moment(selectedMonth.value).format("MMMM YYYY");
                default:
                    return "";
            }
        }

        /**
         * ! Get all presenze
         */
        async function loadPresenze() {
            loading.value = true;
            try {
                // Pass selected month only if 'mese' period is selected
                const monthParam = selectedPeriod.value === 'mese' ? selectedMonth.value : null;
                const response = await apiResponsabile.getPresenze(responsabileID, monthParam);
                
                if (response.status === 200 && response.data.status === 0) {
                    presenze.value = response.data.data || [];
                } else {
                    presenze.value = [];
                    openToast("Si è verificato un errore durante la richiesta delle presenze", "toast_danger");
                }
            } catch (error) {
                console.error(error);
                presenze.value = [];
                openToast("Si è verificato un errore durante la richiesta delle presenze", "toast_danger");
            }
            loading.value = false;
        }
        loadPresenze();

        // Watch for changes in selectedPeriod and selectedMonth to reload data
        watch(selectedPeriod, () => {
            loadPresenze();
        });

        watch(selectedMonth, () => {
            if (selectedPeriod.value === 'mese') {
                loadPresenze();
            }
        });

        /**
         * ! Filter presenze based on selected period and search query
         */
        const searchQuery = ref("");
        const presenzeToShow = computed(() => {
            let filteredPresenze = presenze.value;

            // Filtro per periodo
            const today = moment();
            switch (selectedPeriod.value) {
                case "oggi":
                    filteredPresenze = presenze.value.filter((presenza) => {
                        return moment(presenza.presenze_data_inizio).isSame(today, 'day');
                    });
                    break;
                case "settimana":
                    filteredPresenze = presenze.value.filter((presenza) => {
                        return moment(presenza.presenze_data_inizio).isSame(today, 'week');
                    });
                    break;
                case "mese":
                    filteredPresenze = presenze.value.filter((presenza) => {
                        return moment(presenza.presenze_data_inizio).format("YYYY-MM") === selectedMonth.value;
                    });
                    break;
            }

            // Filtro per ricerca nel nome dipendente
            const searchTerm = searchQuery.value.toLowerCase();
            if (searchTerm) {
                filteredPresenze = filteredPresenze.filter((presenza) => {
                    const nomeMatch = presenza.dipendenti_nome && presenza.dipendenti_nome.toLowerCase().includes(searchTerm);
                    const cognomeMatch = presenza.dipendenti_cognome && presenza.dipendenti_cognome.toLowerCase().includes(searchTerm);
                    const fullNameMatch = `${presenza.dipendenti_nome} ${presenza.dipendenti_cognome}`.toLowerCase().includes(searchTerm);
                    
                    return nomeMatch || cognomeMatch || fullNameMatch;
                });
            }

            return filteredPresenze;
        });

        return {
            //Icons
            loading,
            arrowBackCircle,
            arrowForwardCircle,
            refresh,
            menu,
            presenze,
            dateFormat,
            loadPresenze,
            openMenu,
            presenzeToShow,
            searchQuery,
            // Filtri periodo
            selectedPeriod,
            setActivePeriod,
            getPeriodLabel,
            // Month selector
            selectedMonth,
            setActiveMonth,
            months,
        };
    },
};
